import { useState } from "react";
import { Check, Play, Mic, ThumbsUp, DollarSign, Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CHAPTER_THEME } from "./serviceTheme";

// Services — three color-coded cards (Video / Voz de Marca / Conversión).
// No pricing, no per-card CTA (the page has exactly one CTA, in the
// closing block). Each chapter has a literal icon: play (video), mic
// (voz de marca), thumbs-up + dollar sign ("like con plata" / stonks,
// conversión). Every sub-service is collapsed by default — click it to
// expand "Para quién es" / "Qué incluye"; a +/- mark shows the state.
// Hovering or opening an item tints its text in the chapter's signal
// color (red-orange for video, blue for voz de marca, green for
// conversión) so the interaction reads as one system across the page.
function ChapterIcon({ chapter }) {
  if (chapter.key === "video") {
    return <Play className="w-6 h-6" fill="currentColor" strokeWidth={0} />;
  }
  if (chapter.key === "voz") {
    return <Mic className="w-6 h-6" strokeWidth={2} />;
  }
  return (
    <span className="relative inline-flex" aria-hidden="true">
      <ThumbsUp className="w-6 h-6" fill="currentColor" strokeWidth={0} />
      <DollarSign
        className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-ink"
        strokeWidth={3}
      />
    </span>
  );
}

function ServiceItem({ item, theme, isOpen, onToggle, forQuienLabel, incluyeLabel }) {
  const [hovering, setHovering] = useState(false);
  const tinted = isOpen || hovering;

  return (
    <li className="border-t border-paper/10 pt-4 first:border-t-0 first:pt-0">
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className="w-full flex items-start gap-3 text-left"
        aria-expanded={isOpen}
        data-testid={`service-item-toggle-${item.title}`}
      >
        <span className={`mt-0.5 shrink-0 transition-colors duration-200 ${tinted ? theme.text : "text-paper/70"}`} aria-hidden="true">
          <Check className="w-4 h-4" strokeWidth={2.5} />
        </span>
        <span
          className={`flex-1 font-body font-semibold text-sm md:text-base leading-snug [text-wrap:balance] transition-colors duration-200 ${
            tinted ? theme.text : "text-paper"
          }`}
        >
          {item.title}
        </span>
        <span className={`mt-0.5 shrink-0 transition-colors duration-200 ${tinted ? theme.text : "text-paper/50"}`} aria-hidden="true">
          {isOpen ? <Minus className="w-4 h-4" strokeWidth={2.5} /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-2.5 pl-7 space-y-2.5 pb-1">
              <div>
                <span className={`font-mono text-[9px] uppercase tracking-[0.15em] ${theme.text}`}>
                  {forQuienLabel}
                </span>
                <p className="mt-1 font-body text-xs md:text-sm text-paper/60 leading-relaxed [text-wrap:pretty]">
                  {item.forQuien.join(" ")}
                </p>
              </div>
              <div>
                <span className={`font-mono text-[9px] uppercase tracking-[0.15em] ${theme.text}`}>
                  {incluyeLabel}
                </span>
                <p className="mt-1 font-body text-xs md:text-sm text-paper/60 leading-relaxed [text-wrap:pretty]">
                  {item.incluye.join(" ")}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

export function ServiceCards({ chapters = [], forQuienLabel, incluyeLabel }) {
  const [openItem, setOpenItem] = useState(null); // `${chapterKey}:${itemIndex}` | null

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start" data-testid="service-cards">
      {chapters.map((chapter, i) => {
        const theme = CHAPTER_THEME[chapter.color];
        return (
          <motion.div
            key={chapter.key}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 }}
            whileHover={{ y: -8, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } }}
            className={`group relative flex flex-col overflow-hidden border border-paper/15 bg-paper/[0.03] p-7 md:p-8 transition-colors duration-300 ${theme.hoverBorder}`}
            data-testid={`service-card-${chapter.key}`}
          >
            {/* Ambient signal-color wash — hidden until hover, so the card
                feels like it "wakes up" under the cursor instead of just
                lifting in place. */}
            <span
              className={`pointer-events-none absolute -top-10 -left-10 w-40 h-40 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100 ${theme.glow}`}
              aria-hidden="true"
            />

            <div
              className={`relative w-14 h-14 flex items-center justify-center border ${theme.border} ${theme.text} transition-transform duration-300 group-hover:scale-110`}
            >
              <ChapterIcon chapter={chapter} />
            </div>

            <span className={`relative mt-6 font-mono text-xs tracking-[0.25em] ${theme.text}`}>{chapter.number}</span>
            <h3 className="relative mt-2 font-body font-semibold text-2xl md:text-3xl text-paper leading-snug [text-wrap:balance]">
              {chapter.label}
            </h3>
            <p className="relative mt-3 font-body text-base text-paper/70 leading-relaxed [text-wrap:pretty]">
              {chapter.blurb}
            </p>

            <ul className="relative mt-6 space-y-5 flex-1">
              {chapter.items.map((item, j) => {
                const itemKey = `${chapter.key}:${j}`;
                return (
                  <ServiceItem
                    key={item.title}
                    item={item}
                    theme={theme}
                    isOpen={openItem === itemKey}
                    onToggle={() => setOpenItem((cur) => (cur === itemKey ? null : itemKey))}
                    forQuienLabel={forQuienLabel}
                    incluyeLabel={incluyeLabel}
                  />
                );
              })}
            </ul>

            <span
              className={`pointer-events-none absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-300 ${theme.bg}`}
              aria-hidden="true"
            />
          </motion.div>
        );
      })}
    </div>
  );
}
